// The calendar program on the Clockers 64: four modes on the 40 x 27 screen, chosen with F1-F4.
//   F1 KALENDER  the doors; arrows or the mouse select, RETURN or a click opens
//   F2 SPELEN    the games that have opened, as a list
//   F3 OM        about the project
//   F4 BLOGG     the diary (not published yet)
//
// The screen is drawn into the terminal; alongside it the program keeps an accessible twin in the
// DOM: real buttons for the modes and doors, visually hidden but focusable. Focus on a twin button
// selects the same thing on the screen, so the screen's highlight is the visible focus.

import { GRID, type Terminal } from "./terminal";
import { swedishDate, type Door } from "../games";
import { ABOUT, ABOUT_DRAFT } from "./about";

export type Mode = "calendar" | "games" | "about" | "blog";

const MODES: { mode: Mode; key: string; label: string; name: string }[] = [
  { mode: "calendar", key: "F1", label: "KALENDER", name: "Kalendern" },
  { mode: "games", key: "F2", label: "SPELEN", name: "Spelen" },
  { mode: "about", key: "F3", label: "OM", name: "Om projektet" },
  { mode: "blog", key: "F4", label: "BLOGG", name: "Bloggen" },
];

const COLS = 6;
const BOX_W = 6;
const LEFT = Math.floor((GRID.cols - COLS * BOX_W) / 2);
/** The screen holds at most this many doors (6 x 6 compact boxes); more are ignored with a warning. */
export const MAX_DOORS = 36;
/** Box height and first row: 4-row boxes up to 24 doors, compact 3-row boxes up to 36. */
function layout(n: number): { boxH: number; top: number } {
  if (n <= 24) return { boxH: 4, top: 4 };
  if (n <= 30) return { boxH: 3, top: 4 };
  return { boxH: 3, top: 3 };
}
const INFO_ROW = 21;
const HINT_ROW = 22;
const KEYS_ROW = 25;
const LIST_TOP = 4;
const LIST_ROWS = 15;
const TEXT_TOP = 3;
const TEXT_ROWS = 18;
const TEXT_W = 38;

export interface ProgramHooks {
  /** the visitor opens a door that has a game */
  open(door: Door): void;
}

function center(text: string): number {
  return Math.max(0, Math.floor((GRID.cols - text.length) / 2));
}

function wrap(text: string, width: number): string[] {
  const out: string[] = [];
  let line = "";
  for (const word of text.split(/\s+/)) {
    if (!line) line = word;
    else if (line.length + 1 + word.length <= width) line += " " + word;
    else {
      out.push(line);
      line = word;
    }
  }
  if (line) out.push(line);
  return out;
}

const up = (s: string) => s.toLocaleUpperCase("sv-SE");

export class Program {
  mode: Mode = "calendar";
  private selDay = 1;
  private selGame = 0;
  private listTop = 0;
  private aboutTop = 0;
  private doors: Door[] = [];
  private busy = false;
  private keySpans: { mode: Mode; c0: number; c1: number }[] = [];
  /** the mode button that has keyboard focus in the twin (shown under the F-key row) */
  private focusedMode: Mode | null = null;
  /** the accessible twin; Scene places it inside #screen */
  readonly a11y: HTMLDivElement;

  constructor(private term: Terminal, private hooks: ProgramHooks) {
    this.a11y = document.createElement("div");
    this.a11y.className = "sr-only screen-a11y";
    this.a11y.addEventListener("focusin", (ev) => this.onFocus(ev));
    this.a11y.addEventListener("click", (ev) => this.onA11yClick(ev));
    this.a11y.addEventListener("focusout", (ev) => {
      if (!this.a11y.contains(ev.relatedTarget as Node | null) && this.focusedMode) {
        this.focusedMode = null;
        this.render();
      }
    });
  }

  setDoors(doors: Door[]): void {
    if (doors.length > MAX_DOORS) {
      console.warn(`[julkalenderspel] ${doors.length} doors; the screen shows the first ${MAX_DOORS}`);
      doors = doors.slice(0, MAX_DOORS);
    }
    this.doors = doors;
    if (!doors.some((d) => d.day === this.selDay)) this.selDay = doors[0]?.day ?? 1;
    // start on today's door if there is one, else the latest open, else door 1
    const pick = doors.find((d) => d.today) ?? [...doors].reverse().find((d) => d.state === "open");
    if (pick && this.mode === "calendar" && !this.touched) this.selDay = pick.day;
    this.selGame = Math.min(this.selGame, Math.max(0, this.openGames().length - 1));
    this.render();
    this.buildA11y();
  }

  private touched = false;

  private openGames(): Door[] {
    return this.doors.filter((d) => d.state === "open");
  }

  // ---------- drawing ----------

  /** Draw the whole screen for the current mode. */
  render(): void {
    const t = this.term;
    t.clearGrid();
    if (this.mode === "calendar") this.drawCalendar();
    else if (this.mode === "games") this.drawGames();
    else if (this.mode === "about") this.drawAbout();
    else this.drawBlog();
    this.drawKeys();
    t.cursor = { on: false, col: 0, row: GRID.rows - 1, blink: true };
    t.dirty = true;
  }

  /** The first appearance after RUN: the same screen, revealed a row at a time. */
  async drawIn(step: (ms: number) => Promise<void>): Promise<void> {
    this.render();
    const full = this.term.grid.map((r) => r.map((c) => ({ ...c })));
    const cursor = this.term.cursor;
    this.term.clearGrid();
    this.term.cursor = { ...cursor, on: false };
    for (let r = 0; r < full.length; r++) {
      if (full[r].every((c) => c.ch === " " && !c.inv)) continue;
      this.term.grid[r] = full[r];
      this.term.dirty = true;
      await step(40);
    }
    this.term.cursor = cursor;
    this.term.dirty = true;
  }

  private doorCell(day: number): { col: number; row: number; h: number } {
    const i = this.doors.findIndex((d) => d.day === day);
    const { boxH, top } = layout(this.doors.length);
    return { col: LEFT + (i % COLS) * BOX_W, row: top + Math.floor(i / COLS) * boxH, h: boxH };
  }

  private drawCalendar(): void {
    const t = this.term;
    const n = this.doors.length;
    const head = n ? `JULKALENDERSPEL  1-${n} DECEMBER` : "JULKALENDERSPEL";
    t.put(center(head), 1, head);
    if (!n) {
      t.put(center("KALENDERN GICK INTE ATT LÄSA IN."), 8, "KALENDERN GICK INTE ATT LÄSA IN.");
      t.put(center("LADDA OM SIDAN."), 10, "LADDA OM SIDAN.", { dim: true });
      return;
    }
    for (const d of this.doors) {
      const { col, row, h } = this.doorCell(d.day);
      const sel = d.day === this.selDay;
      const dim = d.state === "locked";
      t.put(col, row, "┌────┐", { dim });
      t.put(col, row + h - 1, "└────┘", { dim });
      const markCh = d.state === "open" ? "▶" : d.state === "empty" ? "…" : " ";
      const num = String(d.day).padStart(2, " ") + (d.today ? "*" : " ");
      // 4-row boxes: number above, mark below; compact 3-row boxes: both on one line
      const rows: Array<[number, string]> =
        h === 4 ? [[1, num.padEnd(4, " ")], [2, ` ${markCh}  `]] : [[1, (num + markCh).padEnd(4, " ")]];
      for (const [k, body] of rows) {
        t.put(col, row + k, "│", { dim });
        t.put(col + 1, row + k, body, { inv: sel, dim: dim && !sel });
        t.put(col + BOX_W - 1, row + k, "│", { dim });
      }
    }
    const d = this.doors.find((x) => x.day === this.selDay);
    if (d) {
      let info = "";
      let hint = "";
      if (d.state === "open" && d.game) {
        info = `${d.day} DEC: ${up(d.game.title)} (${d.game.year})`;
        hint = "RETURN ELLER KLICK = SPELA";
      } else if (d.state === "empty") {
        info = `LUCKA ${d.day}: SPELET KOMMER SNART`;
      } else {
        info = `LUCKA ${d.day} ÖPPNAS ${up(swedishDate(d.release))}`;
      }
      t.put(center(info), INFO_ROW, info.slice(0, GRID.cols));
      if (hint) t.put(center(hint), HINT_ROW, hint, { dim: true });
    }
  }

  private drawGames(): void {
    const t = this.term;
    const games = this.openGames();
    const head = `SPELEN  ${games.length} AV ${this.doors.length} ÖPPNADE`;
    t.put(center(head), 1, head);
    if (!games.length) {
      const first = this.doors[0];
      t.put(center("INGA SPEL ÄR SLÄPPTA ÄN."), 6, "INGA SPEL ÄR SLÄPPTA ÄN.");
      if (first) {
        const l = `FÖRSTA LUCKAN ÖPPNAS ${up(swedishDate(first.release))}.`;
        t.put(center(l), 8, l, { dim: true });
      }
      return;
    }
    if (this.selGame < this.listTop) this.listTop = this.selGame;
    if (this.selGame >= this.listTop + LIST_ROWS) this.listTop = this.selGame - LIST_ROWS + 1;
    games.slice(this.listTop, this.listTop + LIST_ROWS).forEach((d, k) => {
      const i = this.listTop + k;
      const title = up(d.game!.title).slice(0, 27).padEnd(27, " ");
      const line = `${String(d.day).padStart(2, "0")}  ${title} ${d.game!.year}`;
      t.put(2, LIST_TOP + k, line, { inv: i === this.selGame });
    });
    t.put(center("PIL UPP/NER, RETURN = SPELA"), HINT_ROW, "PIL UPP/NER, RETURN = SPELA", { dim: true });
  }

  private aboutLines(): string[] {
    const out: string[] = [];
    ABOUT.forEach((p, i) => {
      if (i) out.push("");
      out.push(...wrap(p, TEXT_W));
    });
    return out;
  }

  private drawAbout(): void {
    const t = this.term;
    const title = ABOUT_DRAFT ? "OM PROJEKTET (UTKAST)" : "OM PROJEKTET";
    t.put(center(title), 1, title);
    const lines = this.aboutLines();
    this.aboutTop = Math.max(0, Math.min(this.aboutTop, lines.length - TEXT_ROWS));
    lines.slice(this.aboutTop, this.aboutTop + TEXT_ROWS).forEach((l, k) => t.put(1, TEXT_TOP + k, l));
    if (lines.length > TEXT_ROWS) t.put(center("PIL UPP/NER = BLÄDDRA"), HINT_ROW, "PIL UPP/NER = BLÄDDRA", { dim: true });
  }

  private drawBlog(): void {
    const t = this.term;
    t.put(center("BLOGGEN"), 1, "BLOGGEN");
    const a = "DAGBOKEN FRÅN ARBETET MED SPELEN";
    const b = "PUBLICERAS SNART.";
    t.put(center(a), 6, a);
    t.put(center(b), 8, b, { dim: true });
  }

  private drawKeys(): void {
    const parts = MODES.map((m) => `${m.key} ${m.label}`);
    const line = parts.join("  ");
    let c = center(line);
    this.keySpans = [];
    MODES.forEach((m, i) => {
      const text = parts[i];
      this.term.put(c, KEYS_ROW, text, { inv: m.mode === this.mode });
      this.keySpans.push({ mode: m.mode, c0: c, c1: c + text.length - 1 });
      // keyboard focus on a mode button (not yet chosen): a line under its key
      if (this.focusedMode === m.mode) this.term.put(c, KEYS_ROW + 1, "▀".repeat(text.length));
      c += text.length + 2;
    });
  }

  // ---------- input ----------

  setMode(m: Mode): void {
    if (this.busy || m === this.mode) return;
    this.mode = m;
    this.render();
    this.buildA11y();
  }

  /** A key from the visitor's keyboard. True if the program used it. */
  key(k: string): boolean {
    if (this.busy) return true;
    const f = MODES.find((m) => m.key === k);
    if (f) {
      this.setMode(f.mode);
      return true;
    }
    this.touched = true;
    if (this.mode === "calendar") {
      const i = this.doors.findIndex((d) => d.day === this.selDay);
      const n = this.doors.length;
      const move = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -COLS, ArrowDown: COLS, Home: -n, End: n }[k];
      if (move !== undefined) {
        this.selectIndex(Math.max(0, Math.min(n - 1, i + move)));
        return true;
      }
      if (k === "Enter") {
        this.activate(this.selDay);
        return true;
      }
    } else if (this.mode === "games") {
      const n = this.openGames().length;
      const move = { ArrowUp: -1, ArrowDown: 1, PageUp: -LIST_ROWS, PageDown: LIST_ROWS, Home: -n, End: n }[k];
      if (move !== undefined && n) {
        this.selGame = Math.max(0, Math.min(n - 1, this.selGame + move));
        this.render();
        this.focusTwin();
        return true;
      }
      if (k === "Enter" && n) {
        this.activate(this.openGames()[this.selGame].day);
        return true;
      }
    } else if (this.mode === "about") {
      const move = { ArrowUp: -1, ArrowDown: 1, PageUp: -TEXT_ROWS, PageDown: TEXT_ROWS, Home: -999, End: 999 }[k];
      if (move !== undefined) {
        this.aboutTop += move;
        this.render();
        return true;
      }
    }
    return false;
  }

  /** The pointer is over grid cell (col, row); `click` when it was a click. */
  pointer(col: number, row: number, click: boolean): void {
    if (this.busy) return;
    if (row === KEYS_ROW) {
      const s = this.keySpans.find((x) => col >= x.c0 && col <= x.c1);
      if (s && click) this.setMode(s.mode);
      return;
    }
    if (this.mode === "calendar") {
      const d = this.doors.find((x) => {
        const c = this.doorCell(x.day);
        return col >= c.col && col < c.col + BOX_W && row >= c.row && row < c.row + c.h;
      });
      if (!d) return;
      if (d.day !== this.selDay) {
        this.touched = true;
        this.selDay = d.day;
        this.render();
        this.focusTwin(); // keyboard focus follows the pointer, so Enter opens what is lit
      }
      if (click) this.activate(d.day);
    } else if (this.mode === "games") {
      const i = this.listTop + (row - LIST_TOP);
      if (row < LIST_TOP || row >= LIST_TOP + LIST_ROWS || i >= this.openGames().length) return;
      if (i !== this.selGame) {
        this.selGame = i;
        this.render();
        this.focusTwin();
      }
      if (click) this.activate(this.openGames()[i].day);
    }
  }

  private selectIndex(i: number): void {
    const d = this.doors[i];
    if (!d) return;
    this.selDay = d.day;
    this.render();
    this.focusTwin();
  }

  private activate(day: number): void {
    if (this.busy) return; // one departure at a time, from any input path
    const d = this.doors.find((x) => x.day === day);
    if (!d || d.state !== "open" || !d.game) return;
    this.busy = true;
    // the computer loads the game: a short LOAD line on the info row, then the hand-off
    const name = up(d.game.id.split("-").pop() ?? d.game.id).slice(0, 16);
    const t = this.term;
    for (const r of [INFO_ROW, HINT_ROW]) t.put(0, r, " ".repeat(GRID.cols));
    t.put(1, INFO_ROW, `LOAD"${name}",8,1`);
    t.put(1, HINT_ROW, "LOADING", { dim: true });
    t.dirty = true;
    this.hooks.open(d);
  }

  /** After a failed hand-off (or coming back), accept input again. */
  ready(): void {
    this.busy = false;
    this.render();
  }

  // ---------- the accessible twin ----------

  private buildA11y(): void {
    const had = this.a11y.contains(document.activeElement);
    const root = this.a11y;
    root.replaceChildren();
    const nav = document.createElement("nav");
    nav.setAttribute("aria-label", "Datorns lägen");
    for (const m of MODES) {
      const b = document.createElement("button");
      b.type = "button";
      b.dataset.mode = m.mode;
      b.textContent = `${m.key}: ${m.name}`;
      if (m.mode === this.mode) b.setAttribute("aria-current", "page");
      nav.append(b);
    }
    root.append(nav);
    const h = document.createElement("h2");
    h.textContent = MODES.find((m) => m.mode === this.mode)!.name;
    root.append(h);
    if (this.mode === "calendar") {
      const ul = document.createElement("ul");
      for (const d of this.doors) {
        const li = document.createElement("li");
        const b = document.createElement("button");
        b.type = "button";
        b.dataset.day = String(d.day);
        if (d.state === "open" && d.game) b.textContent = `Lucka ${d.day}: ${d.game.title} från ${d.game.year}. Spela.`;
        else if (d.state === "empty") b.textContent = `Lucka ${d.day}: spelet kommer snart.`;
        else b.textContent = `Lucka ${d.day}: öppnas ${swedishDate(d.release)}.`;
        if (d.state !== "open") b.setAttribute("aria-disabled", "true");
        li.append(b);
        ul.append(li);
      }
      root.append(ul);
    } else if (this.mode === "games") {
      const games = this.openGames();
      if (!games.length) {
        const p = document.createElement("p");
        p.textContent = this.doors[0] ? `Inga spel är släppta än. Första luckan öppnas ${swedishDate(this.doors[0].release)}.` : "Inga spel än.";
        root.append(p);
      } else {
        const ul = document.createElement("ul");
        games.forEach((d) => {
          const li = document.createElement("li");
          const b = document.createElement("button");
          b.type = "button";
          b.dataset.day = String(d.day);
          b.textContent = `Lucka ${d.day}: ${d.game!.title} från ${d.game!.year}. Spela.`;
          li.append(b);
          ul.append(li);
        });
        root.append(ul);
      }
    } else if (this.mode === "about") {
      if (ABOUT_DRAFT) {
        const p = document.createElement("p");
        p.textContent = "Utkast.";
        root.append(p);
      }
      for (const para of ABOUT) {
        const p = document.createElement("p");
        p.textContent = para;
        root.append(p);
      }
    } else {
      const p = document.createElement("p");
      p.textContent = "Dagboken från arbetet med spelen publiceras snart.";
      root.append(p);
    }
    if (had) this.focusTwin(true);
  }

  /** Keep the twin's focus on what the screen shows as selected (only if focus is in the twin). */
  private focusTwin(force = false): void {
    if (!force && !this.a11y.contains(document.activeElement)) return;
    let sel: string | null = null;
    if (this.mode === "calendar") sel = `button[data-day="${this.selDay}"]`;
    else if (this.mode === "games") sel = `button[data-day="${this.openGames()[this.selGame]?.day}"]`;
    else sel = `button[data-mode="${this.mode}"]`;
    const el = (sel && this.a11y.querySelector<HTMLButtonElement>(sel)) || this.a11y.querySelector("button");
    el?.focus({ preventScroll: true });
  }

  private onFocus(ev: FocusEvent): void {
    const b = ev.target as HTMLElement;
    const fm = (b.dataset.mode as Mode | undefined) ?? null;
    if (fm !== this.focusedMode) {
      this.focusedMode = fm;
      this.render();
    }
    const day = Number(b.dataset.day);
    if (!day) return;
    if (this.mode === "calendar" && day !== this.selDay) {
      this.selDay = day;
      this.touched = true;
      this.render();
    } else if (this.mode === "games") {
      const i = this.openGames().findIndex((d) => d.day === day);
      if (i >= 0 && i !== this.selGame) {
        this.selGame = i;
        this.render();
      }
    }
  }

  private onA11yClick(ev: MouseEvent): void {
    const b = (ev.target as HTMLElement).closest("button");
    if (!b) return;
    if (b.dataset.mode) this.setMode(b.dataset.mode as Mode);
    else if (b.dataset.day) this.activate(Number(b.dataset.day));
  }

  /** For the checks. */
  info(): object {
    return { mode: this.mode, selDay: this.selDay, selGame: this.selGame, busy: this.busy, open: this.openGames().map((d) => d.day) };
  }
}
